import { UnexpectedApiResponseError } from 'src/Libs/errors'
import { ApiResult } from 'src/Libs/api'
import { AuthFetchWithResponse } from 'src/Types/api'
import { StatusCodes } from 'src/Libs/http-status-codes'
import { WallboxConfigurationModel } from 'src/Libs/wallboxes'
import { ConnectedDevicesDto, WallboxConfigurationDto, WallboxOcppDto } from 'src/Libs/wallboxes/dto'
import { fromOcppDtoToModelTransformer, fromSettingsDtoToModel, loadPrioDtoToModel, loadPrioFormToDto } from 'src/Libs/wallboxes/transformers'
import { LoadingPriorisationDto } from '../dto'
import { LoadingPriorisationFormModel, LoadingPriorisationModel, WallboxHardwareModel, WallboxOcppModel } from 'src/Libs/wallboxes/models'
import { handleFetchError } from 'src/Libs/api/utils'
import { AuthFetchError, AuthFetchWithResult } from '@e3dc-react/shell/Types/api'
import { AddWallboxDto } from '../dto/add-wallbox.dto'
import { WallboxHardwareDto } from '../dto/wallbox-hardware.dto'
import { fromHardwareDtoToModel } from '../transformers/wallbox-hardware'
import { WallboxStandardConfigurationDto } from '../dto/wallbox-configuration-update.dto'

export type ConnectedWallbox = {
  storageSerialNo: string
  wallboxIndex: number
}

/**
 * Returns the index values of all connected wallbox devices.
 *
 * The actual API result can be a single number, or a list of numbers. So this function has to streamline
 * this behavior.
 *
 * @param serialNumber
 * @param authFetch
 */
export const getWallboxesByStorage = async (
  serialNo: string | undefined,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<ConnectedWallbox[], AuthFetchError | Error>> => {
  if (!serialNo) return new ApiResult<ConnectedWallbox[], AuthFetchError | Error>([], true)
  try {
    const response = await authFetch<ConnectedDevicesDto>({
      path: `wallboxes/${serialNo}/connected-devices`,
      method: 'GET',
    })

    if (response.status === StatusCodes.OK) {
      const indexData = response.data.index as unknown
      const indexes = typeof indexData === 'number' ? [indexData] : (indexData as number[])

      const transformedData = indexes.map(wallboxIndex => ({
        storageSerialNo: serialNo,
        wallboxIndex,
      }))

      return new ApiResult<ConnectedWallbox[], AuthFetchError | Error>(transformedData, false)
    }
    if (response.status === StatusCodes.NO_CONTENT) return new ApiResult<ConnectedWallbox[], AuthFetchError | Error>([], false)
    return new ApiResult<ConnectedWallbox[], AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError(error)
  }
}

/**
 * Returns the index values of all connected wallbox devices of all clients for a farm storage.
 *
 * The actual API result can be a single number, or a list of numbers. So this function has to streamline
 * this behavior.
 *
 * @param serialNumber
 * @param authFetch
 */
export const getWallboxesByStorageMaster = async (
  serialNo: string | undefined,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<ConnectedWallbox[], AuthFetchError | Error>> => {
  try {
    const response = await authFetch<{ [key: number]: number[] }>({
      path: `wallboxes/${serialNo}/connected-devices/master`,
      method: 'GET',
    })

    if (response.status === StatusCodes.OK) {
      const transformedData = Object.entries(response.data).flatMap(([storageSerialNo, wallboxIndexes]) => {
        return wallboxIndexes.map(wallboxIndex => ({
          storageSerialNo,
          wallboxIndex,
        }))
      })

      return new ApiResult<ConnectedWallbox[], AuthFetchError | Error>(transformedData)
    }
    if (response.status === StatusCodes.NO_CONTENT) return new ApiResult<ConnectedWallbox[], AuthFetchError | Error>([], false)
    return new ApiResult<ConnectedWallbox[], AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError(error)
  }
}

export const getWallboxDetails = async (
  serialNumber: string,
  wallboxId: number,
  authFetch: AuthFetchWithResponse,
  enabled = true
): Promise<ApiResult<WallboxConfigurationModel>> => {
  if (!enabled) return new ApiResult<WallboxConfigurationModel>(null, false)
  try {
    const response = await authFetch<WallboxConfigurationDto>({
      path: `wallboxes/${serialNumber}/${wallboxId}/configuration`,
      method: 'GET',
      payload: undefined,
    })

    if (response.status === StatusCodes.OK) {
      const { data } = response
      const model = fromSettingsDtoToModel(data)
      return new ApiResult<WallboxConfigurationModel>(model)
    }
    return new ApiResult<WallboxConfigurationModel>(null, true)
  } catch (error) {
    return new ApiResult<WallboxConfigurationModel>(null, true, error)
  }
}

/**
 * Fetches the OCPP settings for a specific wallbox.
 *
 * @param storageId - storage id of a SSI or a farm client
 * @param wallboxId
 * @param authFetch
 * @returns
 */
export const getWallboxOcppSettings = async (
  storageId: string | undefined,
  wallboxId: string | undefined,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<WallboxOcppModel>> => {
  if (!storageId || wallboxId === undefined) throw new Error('Serial number and wallbox ID must be defined')

  try {
    const response = await authFetch<WallboxOcppDto>({
      path: `wallboxes/${storageId}/${wallboxId}/configuration/ocpp`,
      method: 'GET',
    })

    if (response.status === StatusCodes.OK) {
      const { data } = response
      const model = fromOcppDtoToModelTransformer(data)
      return new ApiResult<WallboxOcppModel>(model, false)
    }

    return new ApiResult<WallboxOcppModel>(null, true)
  } catch (error) {
    throw new ApiResult<WallboxOcppModel>(null, true, error as Error)
  }
}

/**
 * Updates the OCPP settings for a specific wallbox.
 *
 * @param serialNumber
 * @param wallboxId
 * @param model
 * @param authFetch
 */
export const updateWallboxOcppSettings = async (
  serialNumber: string | undefined,
  wallboxId: string | undefined,
  model: WallboxOcppDto | null,
  authFetch: AuthFetchWithResponse
): Promise<void> => {
  if (!serialNumber || !wallboxId) throw new Error('Serial number and wallbox ID must be defined')
  if (!model) throw new Error('OCPP model must be defined')

  const response = await authFetch<void>({
    path: `wallboxes/${serialNumber}/${wallboxId}/configuration/ocpp`,
    method: 'PUT',
    payload: model,
  })
  if (response.status !== StatusCodes.NO_CONTENT) {
    throw new UnexpectedApiResponseError(response.status)
  }
}

export const updateWallboxSettings = async (
  serialNumber: string,
  wallboxId: string,
  model: WallboxStandardConfigurationDto,
  authFetch: AuthFetchWithResponse
): Promise<void> => {
  const response = await authFetch<void>({
    path: `wallboxes/${serialNumber}/${wallboxId}/configuration`,
    method: 'PUT',
    payload: model,
  })
  if (response.status !== StatusCodes.NO_CONTENT) {
    throw new UnexpectedApiResponseError(response.status)
  }
}

export const addWallbox = async (serialNumber: string, model: AddWallboxDto, authFetch: AuthFetchWithResponse): Promise<ApiResult<number>> => {
  const response = await authFetch<void>({
    path: `wallboxes/${serialNumber}/add`,
    method: 'POST',
    payload: JSON.stringify(model),
  })
  if (response.status === StatusCodes.CREATED) {
    return new ApiResult<number>(response.data as unknown as number, false)
  }

  return new ApiResult<number>(null, true)
}

export const getWallboxLoadPrioSettings = async (
  serialNumber: string,
  authFetch: AuthFetchWithResponse,
  enabled: boolean
): Promise<ApiResult<LoadingPriorisationModel>> => {
  if (!enabled) return new ApiResult<LoadingPriorisationModel>(null, false)

  const response = await authFetch<void>({
    path: `wallboxes/${serialNumber}/loading-priorisation`,
    method: 'GET',
  })
  if (response.status === StatusCodes.OK) {
    const model = loadPrioDtoToModel(response.data as unknown as LoadingPriorisationDto)
    return new ApiResult<LoadingPriorisationModel>(model, false)
  }

  return new ApiResult<LoadingPriorisationModel>(null, true)
}

export const updateWallboxLoadPrioSettings = async (
  serialNumber: string,
  model: LoadingPriorisationFormModel,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<boolean>> => {
  const dto = loadPrioFormToDto(model)
  const response = await authFetch<void>({
    path: `wallboxes/${serialNumber}/loading-priorisation`,
    method: 'PUT',
    payload: JSON.stringify(dto),
  })
  if (response.status === StatusCodes.NO_CONTENT) {
    return new ApiResult<boolean>(false, false)
  }

  return new ApiResult<boolean>(false, true)
}

/* TODO: Reuse wherever wallboxes are fetched. Most notably "useWallboxes". Check with dev team if AuthFetchWithResponse is definitely needed or
 * implementation can be kept simple with AuthFetchWithResult and if business logic which relies on status codes can be delegated to react-query/useFetch
 * only where needed.
 */
export const fetchWallboxesByStorage = (storageId: string, authFetch: AuthFetchWithResult): Promise<WallboxHardwareModel[]> => {
  return authFetch<WallboxHardwareDto>({
    path: `wallboxes/${storageId}`,
    endpoint: process.env.REACT_APP_EMOBILITY_ENDPOINT,
  }).then(fromHardwareDtoToModel)
}

/* TODO: Reuse wherever wallboxes are fetched in bulk. Same question to be answered as seen in "fetchWallboxesByStorage" */
export const fetchWallboxesByStorageInBulk = (masterStorageIds: string[], authFetch: AuthFetchWithResult): Promise<WallboxHardwareModel[]> => {
  return authFetch<WallboxHardwareDto>({
    path: `wallboxes/bulk`,
    endpoint: process.env.REACT_APP_EMOBILITY_ENDPOINT,
    payload: {
      sn: masterStorageIds,
    },
  }).then(fromHardwareDtoToModel)
}
